import type { DataRecordDetailComponent, DataRecordField } from "../../types";
import { isParagraphHtml, sanitizeRichHtml } from "../../utils/paragraphHtml";
import { RuntimeImage } from "../../runtime/RendererRuntime";
import { resolveImageValue } from "../../utils/imageOptions";
import { DataRecordPdfPreview } from "./DataRecordPdfPreview";
import { PageTitle } from "./PageTitle";
import { VideoEmbedRenderer } from "./VideoEmbedRenderer";

/**
 * Generic detail block for a single dated data record.
 *
 * One component covers every APA-Radar data type — rate values, FX rates,
 * multi-field statistics rows, PDF-bearing publications — so the dynamic-path
 * deploy engine can render any `page.json` it materialises with a single
 * authored template.
 *
 * The component is authored once as a template page: `recordDate`, every
 * `fields[].value`, `pdfKey` and `body` carry `{token}` placeholders that the
 * deploy engine substitutes per record. A record missing a field leaves the
 * literal `{token}` behind — so at render time any value that is still an
 * unsubstituted token (or empty) is dropped: empty field rows are skipped, and
 * an unresolved `pdfKey`/`body` is treated as absent.
 *
 * Shared `PageTitle` header + record date meta line + two-column field table +
 * PDF preview + rich-text body. The title reuses the canonical `PageTitle`
 * component (the same primary-blue `#00B862` Aleo `<h1>` with a yellow
 * `#FFC105` underline that `PageHeaderRenderer` renders) so a migrated record
 * page reads identically to an authored page. The content renders directly on
 * the white page shell — exactly like an editorial `PageHeader` + `RichText`
 * page — with no surrounding card chrome; the page shell already supplies the
 * container and padding. Colours follow the site system exactly — primary blue
 * `#00B862` for headings and field values, body navy `#121212`, muted
 * blue-grey `#6B7280` for the record-date / field-label / source meta text,
 * `slate-100` for the subtle field-row dividers. The PDF preview reuses the
 * same iframe pattern (`#view=Fit&zoom=75`, download + open-in-new-tab links)
 * used by the admin file picker so the preview is identical everywhere.
 */

/** True when the value is empty or still an unsubstituted `{token}` placeholder. */
function isUnresolved(value?: string | null): boolean {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return true;
  return /^\{.*\}$/.test(trimmed);
}

/** Keeps only field rows with a real, substituted value. */
function visibleFields(fields: DataRecordField[] | undefined): DataRecordField[] {
  return (fields ?? []).filter((f) => f && !isUnresolved(f.value));
}

/**
 * True when a field value is purely numeric (digits + separators) — rates,
 * amounts, percentages. Numbers read best right-aligned with tabular figures;
 * text values (addresses, names) read best left-aligned.
 */
function isNumericValue(value: string): boolean {
  return /^[\d\s.,%+\-/()]+$/.test(value.trim());
}

/**
 * True when a field carries a video `embedUrl` — the `videos` content type's
 * `embedUrl` field. Matched by label (the field's authored title "URL embed",
 * or the raw key `embedUrl`), so a `videos` record renders an inline player
 * instead of a flat URL table row.
 */
function isVideoField(field: DataRecordField): boolean {
  const label = (field.label ?? "").trim().toLowerCase();
  return label === "url embed" || label === "embedurl" || label === "url embed video";
}

/**
 * True when a field is the record's own title — it is already rendered as the
 * page heading (`PageTitle`), so repeating it as a "TITRE" metadata row below
 * the description is pure duplication. Matched by the `title` label or by a
 * value identical to the heading.
 */
function isTitleField(field: DataRecordField, headingTitle: string): boolean {
  const label = (field.label ?? "").trim().toLowerCase();
  if (label === "titre" || label === "title") return true;
  const value = (field.value ?? "").trim();
  return value.length > 0 && value === (headingTitle ?? "").trim();
}

/** Renderer for the generic dated-record detail block. */
export function DataRecordDetailRenderer({ component }: { component: DataRecordDetailComponent }) {
  const { title, typeLabel, recordDate, fields, pdfKey, body, sourceLabel, image, imageAlt, author } = component.content;
  // Per-record title (e.g. "Note de conjoncture hebdomadaire du 17 avril
  // 2026") wins over the shared content-type label when authored. Empty
  // or unresolved `{title}` tokens fall back to typeLabel so the page
  // never shows a literal token or a blank <h1>.
  const headingTitle = title && !isUnresolved(title) ? title.trim() : typeLabel;

  // A `videos` record carries its `embedUrl` as a field — lift it out of the
  // flat field table and render it as an inline 16:9 player instead of a row.
  const resolvedFields = visibleFields(fields);
  const videoField = resolvedFields.find(isVideoField);
  const rows = resolvedFields.filter((f) => f !== videoField && !isTitleField(f, headingTitle));
  const hasPdf = !isUnresolved(pdfKey);
  const hasBody = !isUnresolved(body);
  const dateText = isUnresolved(recordDate) ? "" : recordDate.trim();
  const authorText = isUnresolved(author) ? "" : (author ?? "").trim();
  // Resolve the optional hero/banner image. A storage-key string still carrying
  // an unsubstituted `{token}` resolves to empty → no banner (so the APA-Radar
  // records, which never author `image`, are unchanged).
  const resolvedImage = image ? resolveImageValue(image) : { src: "" };
  const heroSrc = typeof image === "string" && isUnresolved(image) ? "" : resolvedImage.src;

  return (
    <article>
      {/* Canonical site title (reuses PageTitle) + muted record-date / author meta. */}
      <PageTitle title={headingTitle} />
      {dateText || authorText ? (
        <p className="-mt-1 mb-4 text-sm font-medium text-[#6B7280] md:text-base">
          {authorText ? <span className="text-[#00B862]">{authorText}</span> : null}
          {authorText && dateText ? <span className="mx-2 text-[#A1A1AA]">·</span> : null}
          {dateText}
        </p>
      ) : null}

      {/* Optional hero/banner image (blog articles) — wide rounded banner between
          the title/date and the body. Self-hides when unresolved. */}
      {heroSrc ? (
        <div className="apa-img-editorial mb-6 overflow-hidden rounded-2xl bg-[var(--color-surface-2)] shadow-[var(--shadow-e2)] ring-1 ring-black/10">
          <RuntimeImage
            src={heroSrc}
            alt={imageAlt || headingTitle}
            width={1000}
            height={520}
            className="h-auto max-h-[460px] w-full object-cover"
            priority
          />
        </div>
      ) : null}

      {/* Description / rich-text body — directly below the title + date, so a
          publication reads title → date → description → document. */}
      {hasBody ? (
        isParagraphHtml(body as string) ? (
          <div
            className="rich-paragraph-block mb-6 text-base text-[#121212]"
            dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(body as string) }}
          />
        ) : (
          <p className="mb-6 text-base text-[#121212]">{(body as string).trim()}</p>
        )
      ) : null}

      {/* Stacked field list — label above value. The two-column table layout
          made long comma-separated values (taxonomy lists, free-text fields)
          read like a list of fake links; stacking them as a definition list
          gives long values room to wrap and lets each label/value pair read as
          a clean metadata block. */}
      {rows.length > 0 ? (
        <dl className="mb-6 mt-2 divide-y divide-slate-100 border-y border-slate-100">
          {rows.map((field, index) => (
            <div
              key={`${field.label}-${index}`}
              className="py-3.5 first:pt-4 last:pb-4 md:py-4"
            >
              <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-[#6B7280] md:text-sm">
                {field.label}
              </dt>
              <dd
                className={
                  "mt-2 text-base font-medium leading-relaxed text-[#00B862] md:text-lg " +
                  (isNumericValue(field.value) ? "tabular-nums" : "")
                }
              >
                {field.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {/* Video embed — a `videos` record stores its player URL in a field;
          render it as a responsive 16:9 player rather than a flat URL row.
          VideoEmbedRenderer normalises the URL and self-hides on an empty /
          unresolved value, so a non-video record shows nothing. */}
      {videoField ? (
        <div className="mt-6">
          <VideoEmbedRenderer
            component={{
              componentType: "VideoEmbed",
              content: { embedUrl: videoField.value },
            }}
          />
        </div>
      ) : null}

      {/* PDF preview — same iframe behaviour as the rest of the site.
          `pdfKey` is a bucket-relative storage key; DataRecordPdfPreview is a
          client component that resolves it through the runtime `assetResolver`
          (env-driven) into a browser-loadable absolute URL — exactly how
          images resolve via `imageComponent`. It self-hides when the resolved
          URL is empty, so an unresolvable key shows nothing. */}
      {hasPdf ? <DataRecordPdfPreview pdfKey={pdfKey as string} /> : null}

      {/* Optional footer source note. */}
      {sourceLabel && !isUnresolved(sourceLabel) ? (
        <p className="mt-6 border-t border-slate-100 pt-3 text-xs italic text-[#6B7280]">
          {sourceLabel.trim()}
        </p>
      ) : null}
    </article>
  );
}
